import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { CheckCircle2, Building, Target, Zap } from 'lucide-react';

const PricingPage = () => {
  const [isYearly, setIsYearly] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const tiers = [
    {
      id: 'free',
      name: 'Free',
      price: 0,
      description: 'Up to 10 employees → Directory, Org Chart, and 1 Job Posting only',
      buttonText: 'Start Free',
      buttonVariant: 'free',
      features: [
        'Employee directory & profiles',
        'Org chart',
        '1 job posting only',
        'Basic admin controls',
        'Email support',
      ]
    },
    {
      id: 'starter',
      name: 'Starter',
      price: isYearly ? Math.round(6 * 12 * 0.8) : 6,
      description: 'For startups and small teams just getting organized',
      perEmployee: true,
      minimumFee: 49,
      buttonText: 'Get Started',
      buttonVariant: 'primary',
      idealFor: '5–50 employees',
      features: [
        'Employee directory & profiles',
        'Org chart',
        'Time-off management',
        'Onboarding checklists',
        'Basic recruiting/ATS (1 active job post)',
        'Admin controls and roles',
        'Email & Slack integrations (limited)',
      ]
    },
    {
      id: 'growth',
      name: 'Growth',
      price: isYearly ? Math.round(12 * 12 * 0.8) : 12,
      description: 'For scaling teams that want automation and insights',
      perEmployee: true,
      minimumFee: 99,
      buttonText: 'Get Started',
      buttonVariant: 'primary',
      idealFor: '50–250 employees',
      popular: true,
      features: [
        'Everything in Starter plus:',
        'Unlimited job postings (ATS)',
        'Performance management (goals, reviews, 1:1s)',
        'Automation rules (onboarding, HR events)',
        'Policy & document acknowledgments',
        'Basic analytics dashboard',
        'Custom workflows (e.g., custom fields, approval chains)',
      ]
    },
    {
      id: 'intelligence',
      name: 'Intelligence',
      price: isYearly ? Math.round(18 * 12 * 0.8) : 18,
      description: 'For companies that want predictive, data-driven HR',
      perEmployee: true,
      minimumFee: 249,
      buttonText: 'Contact Sales',
      buttonVariant: 'outline',
      idealFor: '250+ employees',
      features: [
        'Everything in Growth plus:',
        'Advanced analytics + "Organizational Memory Graph"',
        'AI insights (attrition prediction, time patterns)',
        'HR trend reports',
        'Cross-tool integrations (Notion, Jira, Slack, Google Workspace)',
        'Advanced permissions and audit trails',
        'Priority support and onboarding help',
      ]
    }
  ];

  const addOns = [
    {
      name: 'Payroll & benefits integration',
      price: '+$3 per employee',
      description: 'Connect with payroll providers and benefits platforms'
    },
    {
      name: 'Custom reporting or API access',
      price: '+$2 per employee',
      description: 'Advanced reporting tools and full API access'
    },
    {
      name: 'Dedicated success manager (enterprise only)',
      price: '$499/month flat',
      description: 'Personal onboarding and ongoing support'
    }
  ];

  const faqItems = [
    {
      question: 'How does the per-employee pricing work?',
      answer: 'You pay per active employee in your system each month. Minimum fees ensure value for smaller teams. The free plan covers up to 10 employees with basic features.',
    },
    {
      question: 'Can I change plans later?',
      answer: 'Yes, you can upgrade or downgrade your plan at any time. Changes take effect immediately and billing is prorated.',
    },
    {
      question: 'What\'s included in the free plan?',
      answer: 'The free plan includes employee directory, org chart, and 1 job posting for up to 10 employees. Perfect for small teams getting started.',
    },
    {
      question: 'Do you offer enterprise pricing?',
      answer: 'Yes, we offer custom solutions for 500+ employees with dedicated support, custom integrations, and negotiated rates ($15-$25 PEPM).',
    },
  ];

  const handleGetStarted = (planCode) => {
    if (authLoading) return;
    
    if (planCode === 'free') {
      if (!user) {
        navigate('/login', { 
          state: { 
            returnTo: '/onboarding', 
            selectedPlan: planCode 
          } 
        });
      } else {
        navigate('/onboarding');
      }
      return;
    }
    
    if (!user) {
      navigate('/login', { 
        state: { 
          returnTo: '/pricing', 
          selectedPlan: planCode 
        } 
      });
      return;
    }

    // Handle paid plan subscriptions
    navigate('/onboarding', { state: { selectedPlan: planCode } });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-700 text-white py-20">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            Choose Your HR Plan
          </h1>
          <p className="text-xl text-purple-100 mb-12 max-w-3xl mx-auto">
            Complete HR platform pricing that scales with your team. Start free, upgrade as you grow.
          </p>
          
          {/* Billing Toggle */}
          <div className="inline-flex items-center bg-white/10 backdrop-blur rounded-lg p-1 mb-8">
            <button
              onClick={() => setIsYearly(false)}
              className={`px-6 py-2 rounded-md transition-all ${
                !isYearly
                  ? 'bg-white text-purple-600 shadow-md'
                  : 'text-white/80 hover:text-white'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setIsYearly(true)}
              className={`px-6 py-2 rounded-md transition-all ${
                isYearly
                  ? 'bg-white text-purple-600 shadow-md'
                  : 'text-white/80 hover:text-white'
              }`}
            >
              Yearly
              <span className="ml-2 text-xs bg-green-400 text-green-900 px-2 py-0.5 rounded-full">
                Save 20%
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Pricing Tiers */}
      <div className="max-w-7xl mx-auto px-6 py-20">
        <div className="grid lg:grid-cols-4 gap-8">
          {tiers?.map((tier) => (
            <div
              key={tier?.id}
              className={`relative bg-white rounded-2xl border-2 transition-all hover:shadow-xl ${
                tier?.popular
                  ? 'border-purple-500 shadow-2xl scale-105'
                  : 'border-gray-200 hover:border-purple-300'
              }`}
            >
              {tier?.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="bg-gradient-to-r from-purple-500 to-indigo-600 text-white px-6 py-2 rounded-full text-sm font-semibold shadow-lg">
                    Most Popular
                  </span>
                </div>
              )}

              <div className="p-8">
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{tier?.name}</h3>
                  
                  <div className="mb-6">
                    {tier?.id === 'free' ? (
                      <div>
                        <span className="text-4xl font-bold text-gray-900">Free</span>
                        <div className="text-sm text-gray-500 mt-2">Up to 10 employees</div>
                      </div>
                    ) : (
                      <div>
                        <span className="text-4xl font-bold text-gray-900">${tier?.price}</span>
                        <span className="text-gray-500 ml-2">
                          {tier?.perEmployee ? ' per employee/' : '/'}
                          {isYearly ? 'year' : 'month'}
                        </span>
                        {tier?.minimumFee && (
                          <div className="text-sm text-gray-600 mt-2">
                            Minimum monthly fee: ${tier?.minimumFee}
                          </div>
                        )}
                        {tier?.idealFor && (
                          <div className="text-sm text-purple-600 font-medium mt-2">
                            🧠 Ideal for {tier?.idealFor}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  
                  <p className="text-gray-600 text-sm mb-6">{tier?.description}</p>
                </div>

                <button
                  onClick={() => handleGetStarted(tier?.id)}
                  disabled={authLoading}
                  className={`w-full py-3 px-6 rounded-lg font-semibold transition-all mb-8 ${
                    tier?.buttonVariant === 'primary' ?'bg-gradient-to-r from-purple-500 to-indigo-600 text-white hover:from-purple-600 hover:to-indigo-700'
                      : tier?.buttonVariant === 'free' ?'bg-green-600 text-white hover:bg-green-700' :'bg-gray-900 text-white hover:bg-gray-800'
                  } disabled:opacity-50`}
                >
                  {authLoading ? 'Loading...' : tier?.buttonText}
                </button>

                <ul className="space-y-3">
                  {tier?.features?.map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <CheckCircle2 className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        {/* Add-ons Section */}
        <div className="mt-20">
          <div className="text-center mb-16">
            <Zap className="h-12 w-12 text-purple-600 mx-auto mb-4" />
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              ⚡️ Add-ons (Optional)
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Enhance your HR platform with additional integrations and premium support.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {addOns?.map((addon, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 hover:shadow-xl transition-all">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{addon?.name}</h3>
                <div className="text-2xl font-bold text-purple-600 mb-3">{addon?.price}</div>
                <p className="text-gray-600 text-sm">{addon?.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Enterprise Section */}
        <div className="mt-16 text-center bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl p-12">
          <Building className="h-12 w-12 text-gray-700 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-4">💼 Enterprise Pricing</h2>
          <p className="text-gray-600 mb-8 max-w-3xl mx-auto">
            Custom quotes for 500+ employees or complex orgs. Usually negotiated at <strong>$15–$25 PEPM</strong> depending on integrations and support.
          </p>
          <button className="bg-gradient-to-r from-purple-600 to-indigo-700 text-white px-8 py-3 rounded-lg font-semibold hover:from-purple-700 hover:to-indigo-800 transition-all">
            Contact Sales
          </button>
        </div>

        {/* Freemium/Entry Plan Callout */}
        <div className="mt-16 text-center bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-12 border-2 border-green-200">
          <Target className="h-12 w-12 text-green-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-4">💡 Freemium / Entry Plan</h2>
          <p className="text-gray-600 mb-8 max-w-3xl mx-auto">
            <strong>Free tier (up to 10 employees)</strong> → Directory, Org Chart, and 1 Job Posting only. 
            This creates viral entry and product-led growth momentum.
          </p>
          <button 
            onClick={() => handleGetStarted('free')}
            className="bg-green-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-green-700 transition-all"
          >
            Start Free (10 employees)
          </button>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="bg-white py-16">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
            Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            {faqItems?.map((faq, index) => (
              <div
                key={index}
                className="border border-gray-200 rounded-lg overflow-hidden"
              >
                <button
                  onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                  className="w-full px-6 py-4 text-left bg-gray-50 hover:bg-gray-100 transition-colors flex justify-between items-center"
                >
                  <span className="font-semibold text-gray-900">{faq?.question}</span>
                  <svg
                    className={`h-5 w-5 text-gray-500 transform transition-transform ${
                      expandedFaq === index ? 'rotate-180' : ''
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
                {expandedFaq === index && (
                  <div className="px-6 py-4 bg-white">
                    <p className="text-gray-700">{faq?.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-700 text-white py-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Transform Your HR Operations?</h2>
          <p className="text-xl text-purple-100 mb-8">
            Join forward-thinking companies using Intime for complete employee lifecycle management.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => handleGetStarted('free')}
              className="bg-white text-purple-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              Start Free (10 employees)
            </button>
            <button
              onClick={() => handleGetStarted('growth')}
              className="bg-transparent border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white/10 transition-colors"
            >
              {user ? 'Upgrade Now' : 'View Plans'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;